import { cacheExchange as cacheExchangeURQL } from '@urql/exchange-graphcache';

import schema from '../../generated/graphql.schema';
import type {
  PartnerPortalContactFragmentFragment,
  SnowFlakeRefundAcknowledgmentVuFragmentFragment,
  SnowflakeRentersPolicyFragmentFragment,
  SnowflakeTuitionPolicyFragmentFragment,
  WaiverCategoryFragmentFragment,
  WaiverQuestionFragmentFragment,
  WaiverContentFragmentFragment,
  WaiverTemplateFragmentFragment,
  WaiverFormFragmentFragment,
  WaiverFormSubmissionFragmentFragment,
  WaiverListFragmentFragment,
  WaiverFormItemFieldFragmentFragment,
} from '../../generated/graphql';
import portalContactMutations from './portalContact';
import zendeskMutations from './zendesk';
import rentersSnowflakeMutations from './rentersSnowflake';
import waiverCategoryMutations from './waiverCategory';
import waiverQuestionMutations from './waiverQuestion';
import waiverContentMutations from './waiverContent';
import waiverTemplateMutations from './waiverTemplate';
import waiverFormMutations from './waiverForm';
import waiverListMutations from './waiverList';

const cacheExchange = cacheExchangeURQL({
  schema,
  updates: {
    Mutation: {
      ...portalContactMutations,
      ...zendeskMutations,
      ...rentersSnowflakeMutations,
      ...waiverCategoryMutations,
      ...waiverQuestionMutations,
      ...waiverContentMutations,
      ...waiverTemplateMutations,
      ...waiverFormMutations,
      ...waiverListMutations,
    },
  },
  keys: {
    PaginatorInfo: () => null,
    PortalContactPaginator: () => null,
    PartnerRolePaginator: () => null,
    PartnerPortalContact: (data) => {
      const contact = data as PartnerPortalContactFragmentFragment;
      return `${contact.partner_id}-${contact.portal_contact_id}`;
    },
    ZDTicketPaginator: () => null,
    ZDTicketComments: () => null,
    ZDTicketUser: () => null,
    PartnerColors: () => null,
    ColorRGB: () => null,
    RentersImplementationPaginator: () => null,
    TuitionImplementationPaginator: () => null,
    // Renters
    SnowFlakePolicyCovered: () => null,
    SnowFlakeRentersDecisions: () => null,
    SnowFlakePolicyRentersTrends: () => null,
    SnowflakeRentersPolicyPaginator: () => null,
    SnowflakeRentersPolicy: (data) => {
      const value = data as SnowflakeRentersPolicyFragmentFragment;
      return value.POLICY_ID;
    },
    // Tuition
    SnowFlakeTuitionPolicyCovered: () => null,
    SnowFlakeTuitionDecisions: () => null,
    SnowFlakePolicyTuitionTrends: () => null,
    SnowflakeTuitionPolicyPaginator: () => null,
    SnowflakeTuitionPolicy: (data) => {
      const value = data as SnowflakeTuitionPolicyFragmentFragment;
      return `${value.POLICY_ID}`;
    },
    RefundPolicyFlow: () => null,
    SnowFlakeRefundAcknowledgmentVuPaginator: () => null,
    SnowFlakeRefundAcknowledgmentVu: (data) => {
      const value = data as SnowFlakeRefundAcknowledgmentVuFragmentFragment;
      return `${value.QUICK_USER_ID}`;
    },
    RefCodeTermPaginator: () => null,
    WaiverCategoryPaginator: () => null,
    WaiverCategory: (data) => {
      const value = data as WaiverCategoryFragmentFragment;
      return value.ulid;
    },
    WaiverQuestionPaginator: () => null,
    WaiverQuestion: (data) => {
      const value = data as WaiverQuestionFragmentFragment;
      return value.ulid;
    },
    WaiverContentPaginator: () => null,
    WaiverContent: (data) => {
      const value = data as WaiverContentFragmentFragment;
      return value.ulid;
    },
    WaiverTemplatePaginator: () => null,
    WaiverTemplate: (data) => {
      const value = data as WaiverTemplateFragmentFragment;
      return value.ulid;
    },
    WaiverTemplateItem: () => null,
    WaiverTemplateItemPayload: () => null,
    WaiverTemplateHtmlPayload: () => null,
    WaiverTemplateRowPayload: () => null,
    WaiverTemplateColumnPayload: () => null,
    WaiverFormPaginator: () => null,
    WaiverForm: (data) => {
      const value = data as WaiverFormFragmentFragment;
      return value.ulid;
    },
    WaiverFormSubmissionPaginator: () => null,
    WaiverFormSubmission: (data) => {
      const value = data as WaiverFormSubmissionFragmentFragment;
      return value.ulid;
    },
    WaiverFormSubmissionQuestion: () => null,
    WaiverFormQuestion: (data) => {
      const value = data as { ulid?: string };
      return value.ulid ?? null;
    },
    WaiverListPaginator: () => null,
    WaiverList: (data) => {
      const value = data as WaiverListFragmentFragment;
      return value.ulid;
    },
    WaiverListOption: (data) => {
      const value = data as { ulid?: string };
      return value.ulid ?? null;
    },
    WaiverDashboardSummary: () => null,
    WaiverDashboardSubmissionVolume: () => null,
    WaiverDashboardSubmissionVolumeBucket: () => null,
    WaiverDashboardFormSubmissions: () => null,
    WaiverDashboardFormSubmissionSegment: () => null,
    WaiverDashboardFormsByCategory: () => null,
    WaiverDashboardFormsByCategorySegment: () => null,
    WaiverDashboardSubmissionsByCategory: () => null,
    WaiverDashboardSubmissionsByCategorySegment: () => null,
    WaiverTemplateItemField: (data) => {
      const value = data as WaiverFormItemFieldFragmentFragment;
      return value.ulid;
    },
    WaiverFormField: (data) => {
      const value = data as WaiverFormItemFieldFragmentFragment;
      return value.ulid;
    },
    // Embedded validation snapshot objects — not globally unique entities.
    WaiverCompiledValidation: () => null,
    WaiverBaseRules: () => null,
    WaiverRule: () => null,
    WaiverConditionalGroup: () => null,
    WaiverRuleCondition: () => null,
  },
});

export default cacheExchange;
